import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import SimpleMultiSelect from '~/components/inputs/Select/SimpleMultiSelect';
import { PaymentMethod } from '~/types/flow';

type NameType<T> = T extends string ? string : T extends {} ? keyof T : string;

interface PaymentMethodFilterProps<Filters extends string | {}> {
  className?: string;
  label?: string;
  placeholder?: string;
  name: NameType<Filters>;
  onChange: (name: NameType<Filters>, value: PaymentMethod[]) => void;
  value: PaymentMethod[];
  hideLabel?: boolean;
  dataTestId?: string;
  clearable?: boolean;
}

const PaymentMethodFilter = <Filters extends string | {}>({
  className,
  label,
  placeholder,
  name,
  onChange,
  value,
  hideLabel,
  dataTestId,
  clearable,
}: PaymentMethodFilterProps<Filters>) => {
  const { t } = useTranslation();

  const options = useMemo(
    () => [
      { label: t('CHECK'), value: PaymentMethod.Check },
      { label: t('POSTAL'), value: PaymentMethod.Postal },
      { label: t('TRANSFER'), value: PaymentMethod.Transfer },
      { label: t('CARD'), value: PaymentMethod.Card },
      { label: t('CASH'), value: PaymentMethod.Cash },
      { label: t('FISCAL_CREDIT'), value: PaymentMethod.FiscalCredit },
      { label: t('TAX_FORM'), value: PaymentMethod.TaxForm },
      { label: t('RIBA'), value: PaymentMethod.Riba },
      { label: t('SDD'), value: PaymentMethod.Sdd },
      { label: t('DEFERRED'), value: PaymentMethod.Deferred },
      { label: t('OTHER'), value: PaymentMethod.Other },
    ],
    [t],
  );

  return (
    <SimpleMultiSelect
      className={className}
      label={label ?? t('FILTER_PAYMENT_METHOD_LABEL')}
      placeholder={placeholder ?? label ?? t('FILTER_PAYMENT_METHOD_LABEL')}
      name={name}
      clearable={clearable}
      value={value}
      hideLabel={hideLabel}
      dataTestId={dataTestId}
      onChange={(val) => onChange(name, val as PaymentMethod[])}
      options={options}
      renderMultipleSelectionValue={(selected) => t('FILTER_PAYMENT_METHODS_SELECTED', { count: selected.length })}
    />
  );
};

export default PaymentMethodFilter;
